import { StyleSheet } from "react-native";
import type { UiTheme } from "../theme";
import { scaled } from "../pictureScale";

// Shared StyleSheet for the Tier-1 progress panels (Movement balance,
// Pain × volume, E1RM trends). Lifted from mobile's TierOnePanels.makeStyles.
export function makeTierOneStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: scaled(theme, 1),
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s3,
    },
    cardError: { borderColor: p.danger },
    cardTitle: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      letterSpacing: scaled(theme, 0.6),
      textTransform: "uppercase",
    },
    empty: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontStyle: "italic",
    },
    errorText: {
      color: p.danger,
      fontSize: ty.sizes.sm,
    },
    balanceRow: { gap: s.s1 },
    balanceTextRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: s.s2,
    },
    balanceLabel: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
      flex: 1,
      minWidth: 0,
    },
    balanceValue: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    barTrack: {
      height: s.s2,
      backgroundColor: p.bg,
      borderRadius: r.r1,
      overflow: "hidden",
    },
    barFill: {
      height: "100%",
      // Comparative data-viz bar (movement balance): neutral, the length
      // carries the datum — off the brand per colour budget S2.5.
      backgroundColor: p.ink3,
    },
    // 4 di padding (8 fra due voci) contro rowGap 0 dentro la voce: e' quel
    // RAPPORTO a dire quali righe sono la stessa cosa, non la distanza in se.
    // Con i 2 di prima, una voce andata a capo era distante dalla propria
    // seconda riga quanto dalla voce sotto, e i volumi si potevano attribuire
    // al muscolo sbagliato.
    painRow: { paddingVertical: s.s2 },
    // ⚠️ DS-49 — questa riga porta QUATTRO cose e su un telefono non ci stanno.
    // Pallino + severità in maiuscolo + muscolo + due volumi in monospazio: di
    // questi solo il muscolo si restringe (`balanceLabel` ha flex:1), gli altri
    // hanno larghezza intrinseca, quindi senza `flexWrap` la somma sfora la card.
    // Riportato da Antenore il 18/08 come baco vecchio, mai corretto.
    //
    // ⛔ La cura NON è troncare i volumi: un numero tagliato non è un numero
    // meno preciso, è un numero SBAGLIATO letto come giusto. Con il wrap i
    // volumi scendono su una riga loro quando non ci stanno, e restano interi.
    // Su schermo largo non cambia niente, perché il wrap non si attiva.
    painLabelRow: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      columnGap: s.s2,
      // ⛔ rowGap 0, e non e' una svista. `gap` in RN vale su ENTRAMBI gli
      // assi: lasciandolo a s2 (4) le due righe di una voce andata a capo
      // sarebbero distanti quanto DUE VOCI diverse (`painRow` ha 2+2 di
      // padding), e due muscoli si leggerebbero come uno. A zero, la riga che
      // va a capo resta attaccata alla sua, come una frase che continua.
      rowGap: 0,
    },
    painDot: { width: 12, height: 12, borderRadius: 6 },
    painSeverity: {
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.3,
      textTransform: "uppercase",
    },
    trendRow: { gap: 4 },
    patternLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.8,
      textTransform: "uppercase",
    },
    fewDataBadge: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontStyle: "italic",
    },
    // The §3 "not measurable yet" sentence is body copy the athlete must read
    // and act on, not a 3-word transient badge — so it gets real body size and
    // the stronger ink2, never the 11px italic fewDataBadge (a11y floor: this
    // is instructional text for a reduced-vision reader).
    notMeasurable: {
      color: p.ink2,
      fontSize: ty.sizes.base,
      lineHeight: ty.sizes.base * ty.leading.base,
    },
    trendLabelRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: s.s2,
    },
    trendFlag: {
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    trendFlagRow: { flexDirection: "row", alignItems: "center", gap: s.s1 },
    // EQ-1-HIST slice 2 — which machine the row's numbers come from, and the
    // way into the exercise's other machines.
    //
    // Left-aligned on its own line under the name rather than beside it: the
    // name is `numberOfLines={1}` and an exercise name plus a machine name is
    // exactly the pair that truncates on a phone, which the a11y floor here
    // forbids (values stay readable and UN-TRUNCATED).
    trendMachineRow: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: s.s2,
    },
    // ink3, sm: this qualifies the headline, it never competes with it. And
    // it is never the brand colour — a machine name is static content
    // (DESIGN.md don't-list: brand is the one primary action and the live
    // state, never a static value).
    trendMachineStatic: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
    },
    // PG-24 — the same voice as the machine line, but it must YIELD. It sits
    // beside a button and carries an exercise NAME, which in German and
    // Russian is long enough to push that button off the card: measured on the
    // visual bench at 393dp, where "Prima Hack squat machine" did exactly that.
    // ⛔ Not `trendMachineStatic` reused: that one qualifies a headline and has
    // nothing next to it to make room for.
    trendCompareCaption: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      flexShrink: 1,
    },
    // The alternates, revealed. Indented so the eye reads them as belonging
    // to the row above; no container, no second card (no card-in-card).
    trendAlternates: {
      gap: s.s1,
      paddingLeft: s.s2,
    },
    trendAlternateRow: {
      flexDirection: "row",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: s.s2,
    },
    trendAlternateName: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      flexShrink: 1,
    },
    // Mono + tabular so two machines' figures line up under each other and
    // can be compared by eye — the whole reason they are grouped.
    trendAlternateValue: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    trendAlternateDetail: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
    },
    sparkRow: {
      flexDirection: "row",
      alignItems: "flex-end",
      height: 24,
      gap: 2,
    },
    sparkBar: {
      flex: 1,
      borderRadius: 1,
      minHeight: 2,
    },
  });
}
